import { useState, useEffect } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import AddButton from "../../../../../components/AddButton";
import EditButton from "../../../../../components/EditButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import {
  addStateLicenseType,
  editStateLicenseType,
  rowForEditLicenseType,
  filterText,
  selectedRowsStateLicenseType,
} from "../atoms/atoms";
import useGetLicenseType from "../hooks/useGetLicenseType";
import useDeactivateLicenseType from "../hooks/useDeleteLicenseType";
import { LicenseTypeForm } from "../forms/LicenseTypeForm";
import { useHistory, useLocation } from "react-router-dom";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import { LicenseTypeInfo } from "../types";

export const LicenseTypeTable = () => {
  const BulkDeletePolicyTypeType = useDeactivateLicenseType();
  const [licenseTypeData, setLicensetypeData] = useState<LicenseTypeInfo[]>([]);

  //Hooks Call
  const {
    data: licenseTypeOriginal,
    isLoading: isFetching,
    isSuccess: licenseTypeSuccess,
  } = useGetLicenseType();

  useEffect(() => {
    if (licenseTypeSuccess && licenseTypeOriginal) {
      setLicensetypeData(licenseTypeOriginal.map((data) => ({ ...data })));
    }
  }, [licenseTypeSuccess, licenseTypeOriginal]);

  const [addState, setAddState] = useRecoilState(addStateLicenseType);
  const [editState, setEditState] = useRecoilState(editStateLicenseType);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateLicenseType
  );
  const setRowToEdit = useSetRecoilState(rowForEditLicenseType);
  const [filterTextState, setFilterTextState] = useRecoilState(filterText);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);
  const [licenseTypeFilteredData, setLicenseTypeFilteredData] = useState<
    LicenseTypeInfo[]
  >([]);

  // permissions

  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission = PERMISSIONS.permission_utilities_licensetype_add;
  const editPermission = PERMISSIONS.permission_utilities_licensetype_edit;
  const deletePermission = PERMISSIONS.permission_utilities_licensetype_delete;

  //For filtered data update
  const [licenseTypeId, setLicenseTypeId] = useState("");

  useEffect(() => {
    if (licenseTypeId?.includes(".")) {
      let ids = licenseTypeId.split(".");
      const arr = ids.map((id) => Number(id));
      const filteredData = licenseTypeData?.filter((x) =>
        arr.includes(x.licenseTypeId)
      );
      setLicenseTypeFilteredData(filteredData);
    } else {
      const filteredData = licenseTypeData?.filter(
        (x) => x.licenseTypeId === Number(licenseTypeId)
      );
      setLicenseTypeFilteredData(filteredData);
    }
  }, [licenseTypeId, licenseTypeData]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state == null) {
    //Do nothing
  } else {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setLicenseTypeFilteredData(result);
          setLicenseTypeId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const filteredItems = licenseTypeData
    ? licenseTypeData?.filter((item) =>
        item.licenseTypeName
          ?.toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
      )
    : [];

  const handleEdit = (rowDetails) => {
    setRowToEdit({ ...rowDetails });
    setEditState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleDelete = () => {
    const ids = selectedRows
      ? selectedRows.map((row) => row.licenseTypeId)
      : undefined;
    BulkDeletePolicyTypeType(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };
  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "License Types" : "License Type"
    }?`}</div>
  );

  const handleAdd = () => {
    setAddState(true);
  };

  const matColumns = [
    {
      title: "License Type Name",
      field: "licenseTypeName",
      customSort: (a, b) =>
        a.licenseTypeName && b.licenseTypeName
          ? a.licenseTypeName.trim().localeCompare(b.licenseTypeName.trim())
          : a.licenseTypeName
          ? 0
          : -1,
      render: (rowData) => rowData.licenseTypeName,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdOn",
      dateSetting: { locale: "en-US", format: "MM/dd/yyyy" },
      render: (rowData) =>
        rowData?.createdOn ? (
          <div className="">
            <div>{new Date(rowData?.createdOn).toLocaleDateString()}</div>
          </div>
        ) : (
          ""
        ),
    },
    {
      title: "Modified By",
      field: "updatedBy",
      customSort: (a, b) =>
        a.updatedBy && b.updatedBy
          ? a.updatedBy.trim().localeCompare(b.updatedBy.trim())
          : a.updatedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "updatedOn",
      dateSetting: { locale: "en-US", format: "MM/dd/yyyy" },
      render: (rowData) =>
        rowData?.updatedOn ? (
          <div className="">
            <div>{new Date(rowData?.updatedOn).toLocaleDateString()}</div>
          </div>
        ) : (
          ""
        ),
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex justify-items-center ml-2">
            <EditButton
              className={""}
              disabled={
                editState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {addState || editState ? (
          <div>
            <div></div>
            <LicenseTypeForm />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                moduleDetails={"LicenseType"}
                textInputName={"LicenseType"}
                columnDetails={"License Type"}
                filterText={setFilterTextState}
                disabledButton={
                  editState ||
                  (selectedRows !== undefined && selectedRows.length > 0)
                }
                disabledTextBox={
                  editState ||
                  (selectedRows !== undefined && selectedRows.length > 0)
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <>
                    <AddButton
                      buttonText="New License Type"
                      onClick={() => handleAdd()}
                      disabled={
                        editState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>

            <div>
              <MaterialTableTS
                data={
                  licenseTypeFilteredData?.length > 0
                    ? licenseTypeFilteredData
                    : filterTextState === ""
                    ? licenseTypeData
                    : filteredItems
                }
                showTitle={true}
                columns={matColumns}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={
                  !editState &&
                  !addState &&
                  userPermissions.includes(deletePermission)
                }
                isLoading={isFetching}
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate License Type"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
};
